import React from 'react';
import PropTypes from 'prop-types';
import * as RouteNavigator from '../../containers/RouteNavigator';
import withTimeout from '../../lib/withTimeout';
import FormstackEntityApplicationSkeleton from './FormstackEntityApplicationSkeleton';
import './FormstackEntityApplication.css';

const STATUS = {
  IDLE: 'idle',
  LOADING: 'loading',
  RENDERED: 'rendered',
  ERROR: 'error',
};

const DEFAULT_TIMEOUT = 15000;
const HEIGHT_POLL_INTERVAL = 300;
const DEFAULT_HEIGHT = 600;
// Tallest page measured live on form 6477712 is ~4,000px, so this is ~5x headroom. It exists only
// to bound a runaway, not to size anything real.
const MAX_HEIGHT = 20000;
const FORM_DETECT_INTERVAL = 200;
const FORM_DETECT_MAX_CHECKS = 20;
// Upper bound on how long a completed submission waits for onFormSubmitted before moving on.
// Formstack already holds the form by then, so recording it must never strand the applicant.
const SUBMISSION_CALLBACK_TIMEOUT = 10000;

const CONTACT_US_URL = 'https://www.tradestation.com/contact-us/';

// The URL a srcdoc document reports for itself. Anything else means the frame navigated away.
const SRCDOC_URL = 'about:srcdoc';

// Where the form is expected to land on completion (T027, configured Formstack-side). Because
// this is an AOP path and the srcdoc frame is same-origin with AOP, the redirect is observable
// from inside the frame -- it is the one robust submission signal, unlike the DOM heuristics.
const CONFIRMATION_PATHS = ['/finish-business-application'];

// What a genuine submission control looks like in Formstack's markup. Matching on structure
// rather than on button text is the point: the label is vendor-controlled and PM-editable, so
// a relabelled button silently changed the meaning of the old text test.
const SUBMIT_CONTROL_SELECTOR = 'input[type="submit"], button[type="submit"], .fsSubmitButton';

const ACTION_SELECTOR = 'button, a, input[type="submit"], input[type="button"], .fsSubmitButton';

// FR-008 — the applicant sees this; the internal message goes to RUM via onFormError and is
// never rendered. It is a developer string ("Form did not render within 15000ms") and, on the
// access-error path, reflects wherever the vendor navigated — a raw "Link Expired" is exactly
// what FR-008 forbids surfacing.
const ERROR_MESSAGE = 'Unable to load the application form.';

// FR-019 — what a screen reader announces at each state change.
const ANNOUNCEMENTS = {
  [STATUS.IDLE]: '',
  [STATUS.LOADING]: 'Loading your application form.',
  [STATUS.RENDERED]: 'Application form ready.',
  [STATUS.ERROR]: `${ERROR_MESSAGE} Use the retry button to try again.`,
};

// Announced while a completed submission is recorded, in place of the frame it hides.
const SUBMITTING_ANNOUNCEMENT = 'Submitting your application.';

// The script src is interpolated into an HTML attribute inside srcdoc. It carries the prefill id,
// which comes back from the API, so it is escaped rather than trusted: an unescaped quote would
// break out of the attribute, and because this iframe is same-origin any injected script would
// run with full access to the Ellis session.
function escapeHtmlAttribute(value) {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

// Formstack's hosted-form mode (`body.fsBody.body-hosted-form`) sizes `body` to the viewport and
// scrolls its own inner container, so `documentElement.scrollHeight` only ever echoes the height
// the iframe already has -- verified live: forcing 600/1200/2000/3500px returned exactly
// 600/1200/2000/3500 while the form itself stayed 3196px. Measuring it could therefore never
// satisfy the `height !== iframeHeight` guard, which pinned every applicant at DEFAULT_HEIGHT.
//
// `.fsForm` is the real content: it is not viewport-clamped, it grows and shrinks with the current
// page, and it does not move when the frame resizes -- so it converges in one step and cannot
// oscillate.
//
// Neither obvious simplification works, both checked live on form 6477712:
//   * The scroll container reports max(content, clientHeight), and clientHeight *is* the frame, so
//     once grown it can never shrink -- it read 3984px for 464px of content. Its ~80px of padding
//     is deliberately left unreclaimed rather than depend on a vendor-hashed class name.
//   * `.fsPage` is worse. The pagination controls and Save-and-Resume are siblings of it inside
//     `.fsForm`, so measuring it would clip the Next button off the bottom of a frame that has
//     `overflow: hidden` -- unreachable, on every page. `querySelector('.fsPage')` also returns
//     the *first* page, which is display:none and measures 0.
function measureContentHeight(iframeDoc) {
  const form = iframeDoc.querySelector('.fsForm');
  if (form && form.scrollHeight) return form.scrollHeight;

  // Non-hosted-form markup, or the form is not in the DOM yet.
  return iframeDoc.documentElement.scrollHeight || iframeDoc.body.scrollHeight;
}

class FormstackEntityApplication extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      status: STATUS.IDLE,
      errorMessage: null,
      iframeHeight: DEFAULT_HEIGHT,
      loadAttempt: 0,
      // Presentational only, and kept out of STATUS on purpose: every load branch keys on
      // RENDERED, and the cross-origin one would read any other status as an access error.
      submitting: false,
    };
    // Frozen at mount on purpose. `render` now runs on every height change, so recomputing the
    // srcdoc there would rebuild the iframe's document if a prop ever changed, discarding whatever
    // the applicant had already typed. The iframe is keyed on `loadAttempt`, so Retry still mounts
    // a fresh element and reloads normally. This makes shouldComponentUpdate's stated guarantee
    // true by construction rather than by the accident of render never running twice.
    this.srcdoc = this.buildSrcdoc();
    this.iframeRef = React.createRef();
    this.errorRef = React.createRef();
    this.timeoutId = null;
    this.heightInterval = null;
    this.formDetectInterval = null;
    this.observer = null;
    this.clickTarget = null;
    this.frameWindow = null;
    this.loadStartTime = null;
    this.lastClickedActionText = '';
    this.lastClickWasSubmit = false;
    this.scrollYBeforeHide = 0;
    this.hasSubmitted = false;
    this.unmounted = false;
    this.handleRetry = this.handleRetry.bind(this);
    this.handleIframeClick = this.handleIframeClick.bind(this);
    this.handleIframePageHide = this.handleIframePageHide.bind(this);
    this.handlePageShow = this.handlePageShow.bind(this);
  }

  componentDidMount() {
    this.loadForm();
    window.addEventListener('pageshow', this.handlePageShow);
  }

  shouldComponentUpdate(nextProps, nextState) {
    // Deliberately ignores prop changes: the container holds the embed back behind a skeleton
    // until the prefill request has settled, so formUrl is final by the time this mounts, and
    // re-rendering a new srcdoc would tear down a form the applicant is part-way through.
    // State changes below still re-render, which is why the srcdoc is frozen in the constructor.
    return this.state.status !== nextState.status
      || this.state.errorMessage !== nextState.errorMessage
      || this.state.iframeHeight !== nextState.iframeHeight
      || this.state.loadAttempt !== nextState.loadAttempt
      || this.state.submitting !== nextState.submitting;
  }

  // WCAG 2.4.3. Both transitions replace the content the applicant was looking at, and neither
  // is initiated by moving focus, so without this focus stays on a detached node and a keyboard
  // or screen-reader user is dropped back at the top of the document. The live region announces
  // the change; this makes it reachable.
  componentDidUpdate(prevProps, prevState) {
    const { status, loadAttempt } = this.state;
    if (status === prevState.status) return;

    if (status === STATUS.ERROR && this.errorRef.current) {
      this.errorRef.current.focus();
      return;
    }

    // `loadAttempt` is only ever non-zero after a retry, and retry is only reachable from the
    // error state. On the first load the applicant has not asked to be moved anywhere, so focus
    // is left where it is. (prevState is LOADING here, not ERROR -- retry passes through IDLE.)
    if (status === STATUS.RENDERED && loadAttempt > 0 && this.iframeRef.current) {
      this.iframeRef.current.focus();
    }
  }

  componentWillUnmount() {
    this.unmounted = true;
    window.removeEventListener('pageshow', this.handlePageShow);
    this.clearTimers();
    this.stopSubmissionDetection();
  }

  // Same-origin access to the embedded document. Returns null instead of throwing once the form
  // navigates cross-origin, which happens on both submit and Save & Resume.
  getIframeDocument() {
    const iframe = this.iframeRef.current;
    if (!iframe) return null;
    try {
      return iframe.contentDocument || iframe.contentWindow.document;
    } catch (e) {
      return null;
    }
  }

  // True once the frame has left the srcdoc document for a real AOP page. The srcdoc document
  // reports a URL of `about:srcdoc`, so any same-origin http(s) URL means a navigation happened
  // -- in practice the T027 confirmation redirect. Reading `location` is wrapped because a
  // part-way navigation can throw, and a throw here must not be mistaken for a submission.
  hasNavigatedToAopPage(iframeDoc) {
    if (this.state.status !== STATUS.RENDERED) return false;
    try {
      const frameLocation = iframeDoc.location;
      if (!frameLocation || !frameLocation.href) return false;
      if (frameLocation.href === SRCDOC_URL) return false;
      return frameLocation.origin === window.location.origin
        && CONFIRMATION_PATHS.indexOf(frameLocation.pathname) !== -1;
    } catch (e) {
      return false;
    }
  }

  clearTimers() {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
    if (this.heightInterval) {
      clearInterval(this.heightInterval);
      this.heightInterval = null;
    }
    if (this.formDetectInterval) {
      clearInterval(this.formDetectInterval);
      this.formDetectInterval = null;
    }
  }

  buildSrcdoc() {
    const { formUrl, idParam } = this.props;
    const separator = formUrl.includes('?') ? '&' : '?';
    const scriptSrc = idParam
      ? `${formUrl}${separator}id=${encodeURIComponent(idParam)}`
      : formUrl;

    // Minimal HTML document that includes the Formstack embed script.
    // document.write works normally in a fresh iframe document.
    return `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<style>
  body { margin: 0; padding: 0; overflow: hidden; font-family: inherit; }
</style>
</head>
<body>
<script src="${escapeHtmlAttribute(scriptSrc)}"></script>
<noscript><p>JavaScript is required to load this form.</p></noscript>
</body>
</html>`;
  }

  loadForm() {
    this.loadStartTime = Date.now();
    this.setState({ status: STATUS.LOADING, errorMessage: null });

    const { loadTimeout } = this.props;
    const timeout = loadTimeout || DEFAULT_TIMEOUT;

    this.timeoutId = setTimeout(() => {
      if (this.state.status === STATUS.LOADING) {
        this.handleError('timeout', `Form did not render within ${timeout}ms`);
      }
    }, timeout);
  }

  applyMeasuredHeight(iframeDoc) {
    const measured = measureContentHeight(iframeDoc);
    if (!measured) return;

    // The old `documentElement` read was self-limiting: it echoed the frame's own height, so it
    // could never ask for anything absurd. Measuring real content removes that property, so the
    // bounds have to be explicit now.
    //
    // The floor matters because the frame is `overflow: hidden` (see the stylesheet): adopting a
    // spuriously small reading would clip the form with no way to scroll to the rest of it. The
    // ceiling stops a feedback loop -- if Formstack ever sizes anything under `.fsForm` in `vh`,
    // each growth would feed the next, every HEIGHT_POLL_INTERVAL, without limit.
    const height = Math.min(Math.max(measured, DEFAULT_HEIGHT), MAX_HEIGHT);
    if (height !== this.state.iframeHeight) {
      this.setState({ iframeHeight: height });
    }
  }

  startHeightPolling() {
    if (this.heightInterval) return;

    this.heightInterval = setInterval(() => {
      const iframeDoc = this.getIframeDocument();
      if (!iframeDoc) {
        // Access lost — the form navigated cross-origin. Stop polling.
        clearInterval(this.heightInterval);
        this.heightInterval = null;
        return;
      }

      this.applyMeasuredHeight(iframeDoc);
    }, HEIGHT_POLL_INTERVAL);
  }

  startSubmissionDetection() {
    const iframeDoc = this.getIframeDocument();
    if (!iframeDoc || !iframeDoc.body || this.observer) return;

    const observer = new MutationObserver(() => {
      if (iframeDoc.querySelector('.fsSubmissionMessage')) {
        this.stopSubmissionDetection();
        this.handleFormSubmitted();
      }
    });

    try {
      observer.observe(iframeDoc.body, { childList: true, subtree: true });
    } catch (e) {
      // `observe` throws if the body is not a Node this realm accepts. That would otherwise land
      // *after* RENDERED was dispatched, leaving a form that looks healthy while submission
      // detection is dead. Bail without adopting the observer so `stopSubmissionDetection` is
      // not left disconnecting something that never watched anything; the cross-origin load and
      // confirmation-redirect signals in `handleIframeLoad` remain as fallbacks.
      return;
    }

    this.observer = observer;

    // Track clicks on interactive elements to distinguish form submission from Save & Resume.
    // The last clicked element's text is checked when the iframe navigates cross-origin.
    this.clickTarget = iframeDoc.body;
    this.clickTarget.addEventListener('click', this.handleIframeClick);

    // Nothing the frame is sent to after a submit can be shown. Formstack redirects it to a page
    // that refuses to be framed, so the browser paints its "… refused to connect" page instead:
    // today that is www.tradestation.com/completeddoc/, and once T027 is configured it will be
    // /finish-business-application, which AOP's frame-ancestors 'none' refuses on staging and
    // production. A load event only arrives once that has already painted. `pagehide` fires as
    // the replacing document is committed, before it can paint, and -- unlike `beforeunload` --
    // only for a navigation that is really happening, so no leave-page prompt can strand the
    // applicant behind a hidden frame.
    this.frameWindow = iframeDoc.defaultView || null;
    if (this.frameWindow) {
      this.frameWindow.addEventListener('pagehide', this.handleIframePageHide);
    }
  }

  stopSubmissionDetection() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
    if (this.clickTarget) {
      this.clickTarget.removeEventListener('click', this.handleIframeClick);
      this.clickTarget = null;
    }
    if (this.frameWindow) {
      try {
        this.frameWindow.removeEventListener('pagehide', this.handleIframePageHide);
      } catch (e) {
        // A submission usually leaves the frame cross-origin, and its window then throws a
        // SecurityError. The listener went with the document it was added to, so nothing leaks.
      }
      this.frameWindow = null;
    }
  }

  handleIframePageHide() {
    if (this.state.status === STATUS.RENDERED && this.lastClickWasSubmit) {
      // Collapsed, the hidden frame no longer holds the page open (see the stylesheet), so the
      // browser clamps the scroll position while it is out of view.
      this.scrollYBeforeHide = window.pageYOffset;
      this.setState({ submitting: true });
    }
  }

  // Leaving AOP unloads the frame too, and the frame's `pagehide` cannot tell that apart from a
  // submit taking it off the form -- so after a Submit the form's own validation stopped, the frame
  // is hidden on the way out. Back or Forward can then restore the page from the back/forward
  // cache exactly as it was left, and a restore fires `pageshow` but no `load` to undo the hide.
  // The scroll position it comes back with is the clamped one, so the applicant is returned to
  // where they left the form -- once the frame is back in view, or the scroll is clamped again.
  handlePageShow() {
    const scrollY = this.scrollYBeforeHide;
    this.revealForm(() => window.scrollTo(0, scrollY));
  }

  // Undoes the early hide when the frame turns out to have landed somewhere other than a
  // submission: hidden over a skeleton for good is a worse dead end than whatever it loaded.
  revealForm(onRevealed) {
    if (this.state.submitting && !this.hasSubmitted) {
      this.setState({ submitting: false }, onRevealed);
    }
  }

  handleIframeClick(e) {
    const target = e.target.closest(ACTION_SELECTOR);
    if (!target) return;
    const actionText = (target.textContent || target.value || '').toLowerCase();
    this.lastClickedActionText = actionText;

    // Save & Resume is rendered as a submit control too, so the label is still consulted -- but
    // only ever to *withhold* forward navigation, never to trigger it.
    const isSubmitControl = typeof target.matches === 'function'
      && target.matches(SUBMIT_CONTROL_SELECTOR);
    this.lastClickWasSubmit = isSubmitControl && !actionText.includes('save');
  }

  handleIframeLoad() {
    const iframeDoc = this.getIframeDocument();

    if (!iframeDoc) {
      // Cross-origin access denied: the frame navigated off-origin. This is a *fallback* signal
      // -- the .fsSubmissionMessage observer and the same-origin confirmation redirect are the
      // primary ones -- so it fails closed. Forward navigation requires positive evidence that a
      // real submit control was used; an unrecognised navigation (a vendor link, an error or
      // expiry redirect, a relabelled button) is treated as unknown and leaves the applicant
      // where they are. Telling someone their regulated account application was submitted when
      // it was not is the worse of the two failures: if this misses a genuine submission the
      // applicant still sees Formstack's own confirmation inside the frame.
      if (this.state.status === STATUS.RENDERED) {
        if (this.lastClickWasSubmit) {
          this.handleFormSubmitted();
        }
      } else {
        this.handleError('access_error', 'Unable to access form content');
      }
      return;
    }

    // The T027 confirmation redirect points at an AOP path, and a srcdoc frame is same-origin
    // with AOP -- so that navigation lands *here*, with a readable document, rather than in the
    // cross-origin branch above. Without this check the frame would quietly boot a second copy
    // of the SPA showing the Thank-You page while the parent stayed on the embed, and the
    // `.fsForm` poll below would simply time out. Escape to the parent instead.
    if (this.hasNavigatedToAopPage(iframeDoc)) {
      this.handleFormSubmitted();
      return;
    }

    this.revealForm();

    const onFormPresent = (doc) => {
      // Size the frame *before* handleFormRendered reveals it. The first poll tick is up to
      // HEIGHT_POLL_INTERVAL away, so without this the applicant sees the form paint at
      // DEFAULT_HEIGHT and then jump by the better part of 3,000px. Takes `doc` rather than
      // closing over `iframeDoc` because the detect-interval path below resolves its own.
      this.applyMeasuredHeight(doc);
      this.handleFormRendered();
      this.startHeightPolling();
      // The form can load again in the same frame -- Back inside it after Save & Resume brings it
      // back as a new document -- and detection still attached to the old one would watch nothing.
      this.stopSubmissionDetection();
      this.startSubmissionDetection();
    };

    // The form might not be in the DOM yet immediately after iframe load, because the Formstack
    // script may still be executing document.write. Poll briefly to detect it.
    if (iframeDoc.querySelector('.fsForm')) {
      onFormPresent(iframeDoc);
      return;
    }

    if (this.formDetectInterval) return;

    let checks = 0;
    this.formDetectInterval = setInterval(() => {
      checks += 1;
      const doc = this.getIframeDocument();

      if (doc && doc.querySelector('.fsForm')) {
        clearInterval(this.formDetectInterval);
        this.formDetectInterval = null;
        onFormPresent(doc);
      } else if (checks > FORM_DETECT_MAX_CHECKS) {
        // Give up quietly — the load timeout owns surfacing this as an error.
        clearInterval(this.formDetectInterval);
        this.formDetectInterval = null;
      }
    }, FORM_DETECT_INTERVAL);
  }

  handleFormRendered() {
    if (this.state.status !== STATUS.LOADING) return;

    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }

    const loadTimeMs = this.loadStartTime ? Date.now() - this.loadStartTime : 0;
    this.setState({ status: STATUS.RENDERED });

    const { onFormLoad } = this.props;
    if (onFormLoad) {
      onFormLoad(loadTimeMs);
    }
  }

  handleError(errorType, errorMessage) {
    this.clearTimers();
    this.setState({ status: STATUS.ERROR, errorMessage });

    const { onFormError } = this.props;
    if (onFormError) {
      onFormError(errorType, errorMessage);
    }
  }

  handleFormSubmitted() {
    // Three independent signals can observe one submission (the .fsSubmissionMessage observer,
    // the same-origin confirmation redirect and the cross-origin navigation fallback), so guard
    // against navigating twice. Timers and listeners are torn down here too: after a submission
    // the frame's document is gone, and height polling against it would otherwise keep running.
    if (this.hasSubmitted) return Promise.resolve();
    this.hasSubmitted = true;

    this.clearTimers();
    this.stopSubmissionDetection();

    // Whatever the frame now shows -- a refused or error page, or Formstack's own confirmation --
    // is not for the applicant, and it would otherwise stay up for as long as onFormSubmitted
    // takes. Covers every detection path, including any the `pagehide` hide did not reach.
    this.setState({ submitting: true });

    // RouteNavigator rather than this.context.router: it is the repo-wide convention (27 modules)
    // and it honours the `redirectTo` query param, which the router context does not. The PDF
    // path makes this same hop through it at EntityApplicationContainer.js. Going through the
    // context also made a missing router a silent no-op, stranding the applicant on the embed
    // after a completed submission.
    const goToConfirmation = () => RouteNavigator.push(CONFIRMATION_PATHS[0]);

    const { onFormSubmitted } = this.props;
    if (!onFormSubmitted) {
      goToConfirmation();
      return Promise.resolve();
    }

    // Waits for the submission to be recorded, as the PDF path waits on paperApplication, so the
    // confirmation page loads the application already submitted. A failure, throw or hang still
    // navigates: Formstack holds the form either way. Leaving the route meanwhile cancels the hop.
    let recorded;
    try {
      recorded = Promise.resolve(onFormSubmitted());
    } catch (e) {
      recorded = Promise.resolve();
    }
    return withTimeout(recorded, SUBMISSION_CALLBACK_TIMEOUT)
      .catch(() => {})
      .then(() => {
        if (!this.unmounted) goToConfirmation();
      });
  }

  handleRetry() {
    this.clearTimers();
    this.stopSubmissionDetection();
    this.lastClickedActionText = '';
    this.lastClickWasSubmit = false;

    // loadAttempt keys the iframe. Without it React reuses the element, and because srcdoc is
    // unchanged the document is never re-fetched — the PoC's retry restarted the timeout but
    // never actually reloaded the form.
    this.setState(
      prevState => ({
        status: STATUS.IDLE,
        errorMessage: null,
        iframeHeight: DEFAULT_HEIGHT,
        loadAttempt: prevState.loadAttempt + 1,
        submitting: false,
      }),
      () => this.loadForm(),
    );
  }

  render() {
    const {
      status, iframeHeight, loadAttempt, submitting,
    } = this.state;

    let iframeClassName = 'fs-embed-iframe';
    if (submitting) {
      iframeClassName += ' fs-embed-iframe--submitting';
    } else if (status !== STATUS.RENDERED) {
      iframeClassName += ' fs-embed-iframe--loading';
    }

    return (
      <div className="fs-embed-scope">
        {/*
          Rendered from the first paint with empty content: a live region inserted into the DOM
          at the same moment as its message is not reliably announced, so it has to already be
          there when the status changes.
        */}
        <div className="sr-only fs-embed-status" role="status" aria-live="polite" aria-atomic="true">
          {submitting ? SUBMITTING_ANNOUNCEMENT : ANNOUNCEMENTS[status]}
        </div>
        {(status === STATUS.LOADING || submitting) && <FormstackEntityApplicationSkeleton />}
        {status === STATUS.ERROR && (
          <div className="fs-embed-error" ref={this.errorRef} tabIndex={-1}>
            <p>
              {ERROR_MESSAGE}
              {' Please try again, or '}
              <a href={CONTACT_US_URL} target="_blank" rel="noopener noreferrer">contact us</a>
              {' for help.'}
            </p>
            <button type="button" onClick={this.handleRetry}>
              Retry
            </button>
          </div>
        )}
        {status !== STATUS.ERROR && (
          <iframe
            key={loadAttempt}
            ref={this.iframeRef}
            srcDoc={this.srcdoc}
            title="Entity Account Application"
            // Datadog Session Replay serializes every DOM attribute, and its censor list covers
            // only title/alt/placeholder/IMG.src/A.href/data-* — `srcdoc` is not on it, and that
            // list is gated on privacy level MASK while this app runs `mask-user-input`. Without
            // this attribute the bearer-style prefill id inside the srcdoc is shipped verbatim to
            // Datadog, contradicting the control stated in lib/configureDatadog.js. HIDDEN makes
            // serializeAttributes return {} for this element. The frame's contents are not
            // recorded either way, so nothing observable is lost from the replay.
            data-dd-privacy="hidden"
            className={iframeClassName}
            // A srcdoc document inherits its parent's policy container, so the operative
            // control is the <meta name="referrer"> in public/index.html. This attribute is
            // belt-and-braces: it costs nothing and keeps the guarantee correct if the embed
            // is ever moved to a real cross-origin `src`, which is the direction Client Hub's
            // Formstack embeds already take. Without it, that change would silently start
            // sending the full AOP URL - which can carry the auth token - to Formstack.
            referrerPolicy="strict-origin-when-cross-origin"
            style={{ height: `${iframeHeight}px` }}
            onLoad={() => this.handleIframeLoad()}
          />
        )}
      </div>
    );
  }
}

FormstackEntityApplication.propTypes = {
  formUrl: PropTypes.string.isRequired,
  idParam: PropTypes.string.isRequired,
  onFormLoad: PropTypes.func,
  onFormError: PropTypes.func,
  // Called once per completed submission; may return a promise that navigation waits on.
  onFormSubmitted: PropTypes.func,
  loadTimeout: PropTypes.number,
};

FormstackEntityApplication.defaultProps = {
  onFormLoad: null,
  onFormError: null,
  onFormSubmitted: null,
  loadTimeout: DEFAULT_TIMEOUT,
};

export {
  STATUS, DEFAULT_TIMEOUT, ERROR_MESSAGE, ANNOUNCEMENTS, SUBMISSION_CALLBACK_TIMEOUT,
};
export default FormstackEntityApplication;
